import { AuthenticationResult } from "@azure/msal-browser";
import Routes from "constants/routes";
import { abortableDelay, delay, IAbortablePromise } from "utils";
import { ApiService } from "./ApiService";
import { API_KEY, SUBSCRIPTION_KEY } from "authConfig";

function getDefaultHeaders(input?: RequestInfo | URL) {
  let defaultHeaders = {
    "Ocp-Apim-Subscription-Key": SUBSCRIPTION_KEY,
  };
  if (typeof input === "string" && input.includes("softwareservicesstaging")) {
    defaultHeaders = {
      "Ocp-Apim-Subscription-Key": API_KEY,
    };
  }
  return defaultHeaders;
}

export function redirectIfUnauthorized(input?: RequestInfo | URL) {
  return (r: AuthenticationResult) => {
    const authorizationHeaderValue = `Bearer ${r.accessToken}`;
    return fetch(`${ApiService.v1Url}/employee/info`, {
      headers: {
        ...getDefaultHeaders(input),
        Authorization: authorizationHeaderValue,
      },
    })
      .then(ensureValid)
      .catch((err) => {
        if (!window.location.href.includes(Routes.unauthorized)) window.location.href = Routes.unauthorized;
      })
      .then((_) => authorizationHeaderValue);
  };
}

function authorized(input: RequestInfo | URL, accessToken?: string, init?: RequestInit): IFetchParams {
  const authorizationHeaderValue = `Bearer ${accessToken}`;
  if (init) {
    init.headers = {
      ...init.headers,
      Authorization: authorizationHeaderValue,
    };
  } else {
    init = {
      headers: {
        Authorization: authorizationHeaderValue,
      },
    };
  }
  return { input, init };
}

export function smFetchAsJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  return smFetch(input, init).then(ensureValid).then(toJson);
}

export function smFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const defaultHeaders = getDefaultHeaders(input);
  if (init) {
    if (init.headers) {
      init.headers = { ...init.headers, ...defaultHeaders };
    } else {
      init.headers = defaultHeaders;
    }
  } else {
    init = {
      headers: defaultHeaders,
    };
  }
  return fetch(input, init).then(ensureValid);
}

export function authorizedFetch(
  input: RequestInfo | URL,
  accessToken: () => Promise<string>,
  init?: RequestInit
): Promise<Response> {
  return accessToken()
    .then((t) => authorized(input, t, init))
    .then(({ input: authorizedInput, init: authorizedInit }) => {
      return smFetch(authorizedInput, authorizedInit);
    });
}

export function authorizedAbortableFetch(
  input: RequestInfo | URL,
  accessToken: () => Promise<string>,
  init?: RequestInit
): IAbortablePromise<Response> {
  const abortController = new AbortController();
  const signal = abortController.signal;
  return {
    abort: () => abortController.abort(),
    ready: authorizedFetch(input, accessToken, {
      ...init,
      signal: signal,
    }),
  };
}

export function authorizedAbortableFetchAsJson<T>(
  input: RequestInfo | URL,
  accessToken: () => Promise<string>,
  init?: RequestInit
): IAbortablePromise<T> {
  const abortController = new AbortController();
  const signal = abortController.signal;
  return {
    abort: () => abortController.abort(),
    ready: authorizedFetch(input, accessToken, {
      ...init,
      signal: signal,
    })
      .then(ensureValid)
      .then(toJson),
  };
}

export function authorizedFetchAsJson<T>(
  input: RequestInfo | URL,
  accessToken: () => Promise<string>,
  init?: RequestInit
): Promise<T> {
  return authorizedFetch(input, accessToken, init).then(ensureValid).then(toJson);
}

export function ensureValid(response: Response): Response | Promise<Response> {
  if (!response.ok) {
    return response.json().then((json) => {
      throw new ApiError(json, response);
    });
  }
  return response;
}

function toJson(response: Response): Promise<any> {
  return response.json();
}

interface IFetchParams {
  input: RequestInfo | URL;
  init: RequestInit;
}

export class ApiError extends Error {
  code: number;
  response?: Response;

  constructor(json: any);
  constructor(json: any, response: Response);
  constructor(code: number, message: string);
  constructor(jsonOrCode: any, messageOrResponse?: Response | string) {
    if (typeof jsonOrCode === "number" && typeof messageOrResponse === "string") {
      super(messageOrResponse);
      this.code = jsonOrCode;
    } else if (typeof messageOrResponse !== "string") {
      super(jsonOrCode.message ?? jsonOrCode.Message ?? "Something unexpected happened. Please try again later.");
      this.response = messageOrResponse;
      this.code = jsonOrCode.code ?? jsonOrCode.Code ?? 0;
    } else {
      super(jsonOrCode.message ?? jsonOrCode.Message);
      this.code = jsonOrCode.code ?? jsonOrCode.Code;
    }
  }
}

export function isApiError(err: Error): err is ApiError {
  return "code" in err && "message" in err;
}

export const apiNotConnected = () =>
  delay(() => {
    throw new ApiError(-1, "API not connected");
  }, 1000);

export const abortableApiNotConnected = () =>
  abortableDelay(() => {
    throw new ApiError(-1, "API not connected");
  }, 1000);

export const getLastSegment = (thePath: string) => thePath.substring(thePath.lastIndexOf("/") + 1);
